import { theme } from 'infra/theme'
import React from 'react'
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  YAxis,
} from 'recharts'
import {
  SubSectionTitle,
  SubSectionWrapper,
} from 'components/startup/startup-styles'
import { BaseText, SmallText } from 'components/common/text'
import { Column, Row } from 'components/common/layout'
import styled from 'styled-components'
import dayjs from 'dayjs'
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { CHANNEL_TALK_URL } from 'infra/constants'
import { useIsDesktop } from 'utils/hooks'
import { StartupMemberHistory } from 'types/common'

export const StartupChart: React.FC<{
  memberHistory: StartupMemberHistory[]
}> = ({ memberHistory }) => {
  const isDesktop = useIsDesktop()

  const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
    active,
    payload,
    label,
  }) => {
    if (!active || !payload || payload.length === 0) return null
    const join = payload.find((p) => p.dataKey === 'join')
    const leave = payload.find((p) => p.dataKey === 'leave')
    const total = payload.find((p) => p.dataKey === 'total')
    return (
      <CustomTooltipContainer>
        <SmallText>
          총 인원 <b>{total?.value ?? '-'}명</b>
        </SmallText>
        <SmallText>
          입사 <b>{join?.value ?? '-'}명</b>
        </SmallText>
        <SmallText>
          퇴사 <b>{leave?.value ?? '-'}명</b>
        </SmallText>
        <SmallText>{dayjs(label as string).format('YYYY.MM')}</SmallText>
      </CustomTooltipContainer>
    )
  }

  return (
    <SubSectionWrapper>
      <Row style={{ justifyContent: 'space-between' }}>
        <SubSectionTitle>Q. 최근 1년간 인원 변화가 궁금해요.</SubSectionTitle>
        <Button
          variant={BtnType.SUPER_LOW_STRONG}
          style={{ width: 'fit-content' }}
          size={BtnSize.SMALL}
          onClick={() => {
            window.open(CHANNEL_TALK_URL, '_blank')
          }}
        >
          {isDesktop ? '인원정보' : ''} 오류 신고
        </Button>
      </Row>
      <ChartContainer>
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart data={memberHistory}>
            <CartesianGrid stroke={theme.color.background} />
            <YAxis
              stroke={theme.color.background}
              width={30}
              tick={(props) => {
                const { x, y, payload, index } = props
                // 짝수 index만 표시
                if (index % 2 !== 0) return <g />
                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor='end'
                    fontSize={theme.fontSize.small}
                    fill={theme.color.gray2}
                  >
                    {payload.value}명
                  </text>
                )
              }}
            />
            <XAxis
              dataKey='date'
              stroke={theme.color.background}
              tick={(props) => {
                const { x, y, payload } = props
                // 짝수 index만 표시
                return (
                  <text
                    x={x}
                    y={y + 16}
                    textAnchor='middle'
                    fontSize={theme.fontSize.small}
                    fill={theme.color.gray2}
                  >
                    {dayjs(payload.value).format('YYYY.MM')}
                  </text>
                )
              }}
            />
            <Tooltip content={CustomTooltip} />
            <Bar
              dataKey='join'
              barSize={20}
              fill={theme.color.secondary}
              name='입사자'
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey='leave'
              barSize={20}
              fill={theme.color.line}
              name='퇴사자'
              radius={[4, 4, 0, 0]}
            />
            <Line
              dot={false}
              dataKey='total'
              stroke={theme.color.primary}
              name='총 인원'
              strokeWidth={1.5}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartContainer>
      <BaseText style={{ color: theme.color.gray1, textAlign: 'end' }}>
        출처: 공공데이터포털
      </BaseText>
    </SubSectionWrapper>
  )
}

const CustomTooltipContainer = styled(Column)`
  gap: 4px;
  background: #fff;
  border: 1px solid ${(p) => p.theme.color.line};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: center;
`

const ChartContainer = styled(Column)`
  gap: 24px;
  width: 100%;
  height: 250px;
  padding: 24px 16px 12px 12px;
  border: 1px solid ${(p) => p.theme.color.background};
  border-radius: 6px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 16px 12px 8px 8px;
  }
`
